import styled from 'styled-components/macro';
import * as Sentry from '@sentry/react';
import { connectToChild } from 'penpal';
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { usePopover } from 'shared/providers/PopoverWindowProvider';
import { IVideoCallRoom } from './interfaces/IVideoCallRoom';
import { differenceInMinutes } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import {
  CallHistoryType,
  CallProvider,
  CallStatus,
  ChannelType,
  useGetClinicChannelPetParentsLazyQuery,
  useGetClinicSettingsGeneralQuery,
} from 'shared/types/graphql';
import { Popover, PopoverSizes } from 'shared/providers/PopoverWindowProvider';
import { PopoverName } from 'shared/enums/PopoverName';
import { AsyncMethodReturns, CallSender } from 'penpal/lib/types';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { RouteBasePaths } from 'routes';
import { env } from 'env';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CREATE_ONE_CALL_HISTORY, CREATE_ONE_CALL_PARTICIPANT } from 'shared/mutations';
import { FIND_UNIQUE_CALL_HISTORY } from 'shared/queries';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { useTracking } from 'shared/providers/TrackingProvider';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { VideoCallEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IVideoCallProps {
  channelId: string;
  clinicPetParentId: string;
  startTop?: number;
  startLeft?: number;
}

const VideoCall = ({ channelId, startLeft, startTop, clinicPetParentId }: IVideoCallProps): JSX.Element => {
  const startTime = useMemo(() => Date.now(), []);
  const toast = useToast();
  const { clinicUser, currentClinicId } = useClinicUser();
  const iframeRef = useRef(null);
  const { closePopover } = usePopover();
  const history = useHistory();
  const URL = env.REACT_APP_VIDEO_CALL_PLUGIN_URL;
  const { isTablet: isTabletSize, currentHeight } = useResolutionProvider();
  const { trackEvent } = useTracking();

  const tempContainerRef = useRef(null); // Ref for the iframe

  const isTablet = useMemo(() => {
    // Check OS
    const isWindows = /Win/i.test(navigator.platform);
    const isMac = /Mac/i.test(navigator.platform);
    const isLinux = /Linux/i.test(navigator.platform);

    return isTabletSize && !(isWindows || isMac || isLinux);

    // We want to run this only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = useParams<{ channelId: string }>();
  const [child, setChild] = useState<AsyncMethodReturns<CallSender, string> | null>(null);
  const [currentRoomSid, setCurrentRoomSid] = useState<string | null>(null);

  const [createOneCallHistory] = useMutation(CREATE_ONE_CALL_HISTORY);
  const [findUniqueCallHistory, { data: fetchedCallHistory, loading }] = useLazyQuery(FIND_UNIQUE_CALL_HISTORY, {
    errorPolicy: GraphQLErrorPolicies.All,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });
  const [createOneCallParticipant] = useMutation(CREATE_ONE_CALL_PARTICIPANT);
  const { data: currentClinic } = useGetClinicSettingsGeneralQuery({
    variables: { id: currentClinicId || '' },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    skip: !currentClinicId,
  });

  const [getChannel, { data }] = useGetClinicChannelPetParentsLazyQuery({
    variables: {
      where: {
        id: channelId || params.channelId,
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const isTeamChatChannel = useMemo(() => {
    return data?.findUniqueChannel?.channelType === ChannelType.Team;
  }, [data?.findUniqueChannel?.channelType]);

  const routeBasePath: RouteBasePaths = useMemo(() => {
    return isTeamChatChannel ? RouteBasePaths.Team : RouteBasePaths.Conversations;
  }, [isTeamChatChannel]);

  const closeVideoCall = useCallback(() => {
    if (!data) return;

    try {
      navigator?.mediaDevices
        ?.getUserMedia({ audio: true, video: true })
        .then((mediaStream) => mediaStream.getTracks().forEach((track) => track.stop()))
        .finally(() => {
          closePopover();
          trackEvent(VideoCallEvents.CallEnded, {
            callTime: `${differenceInMinutes(Date.now(), startTime)} minute(s)`,
          });
          if (isTablet) {
            history.push(`${routeBasePath}/${channelId || params.channelId}`);
          }
        });
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      toast({
        status: 'error',
        title: 'The video connection was not ended successfully',
      });
    }
  }, [data, closePopover, trackEvent, startTime, isTablet, history, routeBasePath, channelId, params.channelId, toast]);

  const setUpVideo = useCallback(() => {
    const iframe = iframeRef.current;

    if ((channelId || params.channelId) && iframe && data && !child) {
      try {
        // Use TeamChat channel name or conversation participants
        const clientName = isTeamChatChannel
          ? data?.findUniqueChannel?.friendlyName || 'Your Team'
          : data?.findUniqueChannel?.channelMembers
              ?.filter(({ clinicPetParent, removedAt }) => clinicPetParent && !removedAt)
              .map((p) => [p.clinicPetParent?.firstName, p.clinicPetParent?.lastName].join(' ').trim())
              .join(', ')
              .trim();

        const connection = connectToChild({
          debug: true,
          iframe,
          methods: {
            getRoomData: (): IVideoCallRoom => {
              trackEvent(VideoCallEvents.CallStarted);
              return {
                userId: clinicUser?.id,
                channelId: channelId || params.channelId,
                clientName,
                appName: 'clinic-web',
                userInfo: clinicUser,
                clinicName: currentClinic?.findUniqueClinic?.name,
                conversationUrl: `${window.location.origin}${routeBasePath}/${channelId}`,
                isClinicUser: true,
                hasEnhancedVideoRecording: !isTeamChatChannel,
              };
            },
            onCallConnect: (roomSid: string) => {
              if (!isTeamChatChannel) {
                setCurrentRoomSid(roomSid);
                findUniqueCallHistory({ variables: { where: { sourceId: roomSid } } });
              }
            },
            onCallEnd: closeVideoCall,
            // eslint-disable-next-line
            // @ts-ignore
            trackEvent: (eventName: string, eventData?: Record<string, unknown>) => trackEvent(eventName, eventData),
          },
        });

        connection.promise
          .then((connectionChild) => {
            setChild(connectionChild);
          })
          .catch((e) => {
            Sentry.captureException(e);
            console.error(e);
          });
        return (): void => {
          connection.destroy();
        };
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
      }
    }
  }, [
    channelId,
    params.channelId,
    data,
    child,
    isTeamChatChannel,
    closeVideoCall,
    trackEvent,
    clinicUser,
    currentClinic?.findUniqueClinic?.name,
    routeBasePath,
    findUniqueCallHistory,
  ]);

  useEffect(() => {
    if (!isTeamChatChannel) {
      if (fetchedCallHistory?.findUniqueCallHistory === null && !loading) {
        //call history record has not been created yet
        const handleCallHistoryCreate = async (): Promise<void> => {
          const { data: callHistory } = await createOneCallHistory({
            variables: {
              data: {
                sourceId: currentRoomSid,
                from: '',
                provider: CallProvider.Twilio,
                type: CallHistoryType.Video,
                status: CallStatus.InProgress,
                clinic: {
                  connect: {
                    id: currentClinicId || '',
                  },
                },
              },
            },
          });
          createOneCallParticipant({
            variables: {
              data: {
                user: {
                  connect: {
                    id: clinicUser?.id,
                  },
                },
                callHistory: {
                  connect: {
                    id: callHistory?.createOneCallHistory?.id,
                  },
                },
              },
            },
          });
          createOneCallParticipant({
            variables: {
              data: {
                clinicPetParent: {
                  connect: {
                    id: clinicPetParentId,
                  },
                },
                callHistory: {
                  connect: {
                    id: callHistory?.createOneCallHistory?.id,
                  },
                },
              },
            },
          });
        };
        handleCallHistoryCreate();
      } else {
        return;
      }
    }
  }, [
    fetchedCallHistory,
    currentClinicId,
    createOneCallHistory,
    currentRoomSid,
    createOneCallParticipant,
    clinicUser,
    clinicPetParentId,
    loading,
    isTeamChatChannel,
  ]);

  useEffect(() => {
    if (!data) getChannel();
  }, [data, getChannel]);

  useEffect(() => {
    if (data) setUpVideo();
  }, [setUpVideo, data]);

  useEffect(() => {
    // cleanup
    return closeVideoCall;
  }, []); //eslint-disable-line

  const endCall = useCallback(async () => {
    try {
      if (child && child.endCall) {
        const callData = await child?.endCall();
        const hasRatedVideoCall = callData?.hasRatedVideoCall;
        if (!hasRatedVideoCall) trackEvent(VideoCallEvents.ViewVideoRatingModal);
      }
    } catch (e) {
      console.error(e);
    } finally {
      closeVideoCall();
    }
  }, [child, closeVideoCall, trackEvent]);

  const content = useMemo(() => {
    // Only create the iframe once
    return (
      <StyledIframe
        title="Otto-Video-Call"
        ref={iframeRef}
        src={URL}
        allow="camera;microphone;fullscreen;display-capture;clipboard-write;"
        frameBorder="0"
        name="video-call-iframe"
      />
    );
  }, [URL]);

  return (
    <>
      {data && (
        <Popover
          name={PopoverName.VideoCall}
          size={PopoverSizes.lg}
          id={PopoverName.VideoCall}
          startTop={startTop}
          startLeft={startLeft}
          borderWidth={PopoverSizes.lg}
          onClose={isTablet ? undefined : endCall}
          isMobile={isTablet}
          isResizable
          zIndex={1500}
          useDynamicPositioning
        >
          <VideoPlugInWrapper ref={tempContainerRef} isTablet={isTablet} currentHeight={currentHeight}>
            {content}
          </VideoPlugInWrapper>
        </Popover>
      )}
    </>
  );
};

export default VideoCall;

interface IVideoPluginWrapper {
  isTablet: boolean;
  currentHeight: number;
}

const VideoPlugInWrapper = styled.div<IVideoPluginWrapper>`
  background-color: hsl(0, 0%, 18%);
  height: ${({ isTablet, currentHeight }): string => (isTablet ? `${currentHeight}px` : '100%')};
  width: 100%;
  z-index: 9999;
`;

const StyledIframe = styled.iframe`
  height: 100%;
  width: 100%;
`;
